import getServerSidePropsFunction from './common/getServerSideProps';
import useGenre from './common/hooks/useGenre';
import useGenreUrlParams from './common/hooks/useGenreUrlParams';
import useGenreEvents from './common/hooks/useGenreEvents';
import GenrePageMobile from './mobile';
import GenrePageDesktop from './desktop';
import { GenrePageProvider } from './common/contexts/page';
import styles from './styles.module.scss';
import Page from '@/components/app/Page';
import { SHARING } from '@/config/assets';
import Meta from '@/components/app/Meta';
import { extractImgFromAssetObj } from '@/helpers/assets';
import { isValidSlugParam } from '@/helpers/routeParamChecks';
import useGenres, { GENRES_ALL_FLATTENED } from '@/react-query/genres/hooks/useGenres';
import useShouldRenderForDesktop from '@/hooks/layout/useShouldRenderForDesktop';
import useShouldRenderForMobile from '@/hooks/layout/useShouldRenderForMobile';
import { createGenrePageDescription, createGenrePageTitle } from '@/helpers/seo/genres';

const Genre = () => {
  const shouldRenderForDesktop = useShouldRenderForDesktop();
  const shouldRenderForMobile = useShouldRenderForMobile();

  // Extract parameters from the url
  const {
    genreSlug,
    isViewingNewShows,
    notFound: notFoundFromUrlParams
  } = useGenreUrlParams();
  
  // Fetch the genres/genre for the current url
  useGenres({ type: GENRES_ALL_FLATTENED });
  const {
    genre,
    parentGenre,
    isError,
    isSuccess,
    notFound,
    refetch: handleRetry
  } = useGenre({ enabled: !notFoundFromUrlParams && !!genreSlug });

  // Check route for valid parameters. It should contain a valid slug parameter.
  const paramsAreValid = !notFoundFromUrlParams && isValidSlugParam(genreSlug);
  const genreNotFound = !paramsAreValid || notFoundFromUrlParams || !!notFound || (isSuccess && !genre?.id);

  // Extract genre details
  const {
    id: genreId,
    title: genreTitle,
    assets: genreAssets
  } = genre;

  // Extract parent genre details
  const {
    id: parentId,
    title: parentTitle
  } = parentGenre;

  // Send page events
  useGenreEvents({
    genreId,
    genreTitle,
    parentId,
    parentTitle,
    isViewingNewShows,
    shouldSend: !notFoundFromUrlParams
  });

  return (
    <Page
      error={ isError }
      headerClassName={ styles.mobileGenreHeader }
      meta={ (
        <Meta
          description={ createGenrePageDescription({
            genre,
            isViewingNewShows,
            augmentDescription: true
          }) }
          notFound={ genreNotFound }
          sharingDescription={ createGenrePageDescription({
            genre,
            isViewingNewShows
          }) }
          sharingImage={ extractImgFromAssetObj({
            assetsObj: genreAssets,
            imageType: SHARING
          }).image }
          title={ createGenrePageTitle({
            genre,
            isViewingNewShows
          }) }
          useTitleRaw
        />
      ) }
      notFound={ genreNotFound }
      onClickRetry={ handleRetry }
      pageId={ genreSlug }
    >
      <GenrePageProvider>
        { shouldRenderForMobile && <GenrePageMobile /> }
        { shouldRenderForDesktop && <GenrePageDesktop /> }
      </GenrePageProvider>
    </Page>
  );
};

export const getServerSideProps = getServerSidePropsFunction;

export default Genre;